import { Button } from "@progress/kendo-react-buttons";
import { Grid, GridCellProps, GridColumn } from "@progress/kendo-react-grid";
import { useState } from "react";
import { ActionDialog, StatusIcon } from ".";
import { getIconPath } from "../../../utilities/textUtilities";
import { CustomGridCell } from "../../grid";
import { Configuration } from "./Configuration";
import { ConfigAction, ConfigStatus } from "./ConfigurationEnums";
import { MarginNameCell } from "./MarginNameCell";

interface IConfigurationsGridProps {
  data: Configuration[];
  childCompanyIds: number[];
  margins: any[];
  marginField: string;
  childMarginField?: string;
  onChange: () => void;
}

export const ConfigurationsGrid = (props: IConfigurationsGridProps) => {
  const { data, childCompanyIds, margins, marginField, childMarginField, onChange } = props;

  const [action, setAction] = useState("");
  const [actionItem, setActionItem] = useState<Configuration | null>(null);

  const onAction = (action: string, item: Configuration) => {
    setAction(action);
    setActionItem(item);
  };

  const onActionComplete = () => {
    setAction("");
    onChange();
  };

  const ProductCell = (props: GridCellProps) => {
    const { dataItem } = props;
    const { commodity, commodityType, instrument, productType, type } = dataItem;

    return (
      <CustomGridCell {...props}>
        <div>
          <div>
            <img
              alt={commodityType}
              src={getIconPath("commodity", commodityType)}
              title={commodityType}
              style={{ width: 16, height: 16, paddingRight: 3 }}
            />
            <span>{commodity}</span>
          </div>
          <div>
            <img
              alt={type}
              src={getIconPath("instrument", productType)}
              title={type}
              style={{ width: 16, height: 16, paddingRight: 3 }}
            />
            <span>{instrument}</span>
          </div>
        </div>
      </CustomGridCell>
    );
  };

  const MarginCell = (props: GridCellProps) => {
    const { dataItem } = props;

    return (
      <CustomGridCell {...props}>
        <MarginNameCell
          name={dataItem.margin}
          marginId={dataItem.marginId}
          marginCompanyId={dataItem.marginCompanyId}
        />
      </CustomGridCell>
    );
  };

  const ChildMarginCell = (props: GridCellProps) => {
    const { dataItem } = props;

    return (
      <CustomGridCell {...props}>
        <MarginNameCell
          name={dataItem.childMargin}
          marginId={dataItem.childMarginId}
          marginCompanyId={dataItem.childMarginCompanyId}
        />
      </CustomGridCell>
    );
  };

  const StatusCell = (props: GridCellProps) => {
    const { dataItem } = props;
    const config = Configuration.create(dataItem, dataItem);
    const value = config.status;

    const status =
      value === ConfigStatus.Margins && dataItem.margin
        ? ConfigStatus.ChildMargins
        : (value as ConfigStatus);

    return (
      <td className="k-table-td">
        <StatusIcon status={status} />
      </td>
    );
  };

  interface ActionsProps {
    cellProps: GridCellProps;
    action: string;
    onActionClick: (action: string, item: Configuration) => void;
  }

  const ActionsCell = ({ cellProps, action, onActionClick }: ActionsProps) => {
    const { dataItem } = cellProps;
    const config = Configuration.create(dataItem, dataItem);
    const disabled = action.length > 0;

    return (
      <td className="k-table-td">
        <div style={{ display: "flex" }}>
          {config.status !== ConfigStatus.Unassigned && (
            <Button
              disabled={disabled}
              icon="pencil"
              size="small"
              themeColor="primary"
              fillMode="outline"
              title="Assign Margin"
              onClick={() => onActionClick(ConfigAction.Edit, config)}
            />
          )}

          {config.configurationId > 0 && childCompanyIds.includes(config.companyId) && (
            <Button
              disabled={disabled}
              icon={`${config.isActive ? "cancel" : "check"}`}
              size="small"
              themeColor="primary"
              fillMode="outline"
              title={`${config.isActive ? "Suspend" : "Activate"}`}
              onClick={() =>
                onActionClick(
                  config.isActive ? ConfigAction.Suspend : ConfigAction.Activate,
                  config,
                )
              }
            />
          )}

          {config.status === ConfigStatus.Unassigned &&
            childCompanyIds.includes(config.companyId) && (
              <Button
                disabled={disabled}
                icon="plus"
                size="small"
                themeColor="primary"
                fillMode="outline"
                title="Assign"
                onClick={() => onActionClick(ConfigAction.Assign, config)}
              />
            )}
        </div>
      </td>
    );
  };

  const ActionsCellWrapper = (props: GridCellProps) => (
    <ActionsCell
      cellProps={props}
      action={action}
      onActionClick={(newAction: string) => onAction(newAction, props?.dataItem)}
    />
  );

  return (
    <>
      <Grid data={data} sortable={true}>
        <GridColumn field="commodity" title="Product" cells={{ data: ProductCell }} />

        <GridColumn field="margin" title="Margin" cells={{ data: MarginCell }} />

        {childMarginField && (
          <GridColumn field="childMargin" title="Broker Margin" cells={{ data: ChildMarginCell }} />
        )}

        <GridColumn field="status" title="Status" cells={{ data: StatusCell }} width={150} />

        <GridColumn
          field="configurationId"
          title="Actions"
          cells={{ data: ActionsCellWrapper }}
          width={150}
        />
      </Grid>

      {action && actionItem && (
        <ActionDialog
          action={action}
          data={{
            item: actionItem,
            margins: margins.filter((m) => !childCompanyIds.includes(m.companyId)),
            marginField,
          }}
          onClose={() => setAction("")}
          onSave={onActionComplete}
        />
      )}
    </>
  );
};
